import datetimeDecorator from 'src/decorators/datetime.decorator';
import { ShiftPatternEntity } from 'src/entities/ShiftPattern.entity';
import { TruckShiftShiftEntity } from 'src/entities/TruckShift.entity';
import * as yup from 'yup';

export type CellEditorMode = 'pattern' | 'custom';

export type CellEditorFormValues = {
  mode: CellEditorMode;
  patternId: number | null;
  startTime: string;
  endTime: string;
  startDate: string;
  endDate: string;
  workingHours: number | '';
  driverId: number | null;
  driverName: string;
};

export type CustomBody = { start_time: string; end_time: string; start_date?: string; end_date?: string; working_available_duration_hours?: number };
export type EditRequestBody = { shift_pattern_id: number } | { custom: CustomBody };
export type AddRequestBody = {
  truck_ids: number[];
  dates: string[];
  mode: 'add';
  shift_pattern_id?: number;
  custom?: CustomBody;
};

export const isPatternEditable = (pattern: Pick<ShiftPatternEntity, 'multiDay' | 'slots'>): boolean =>
  !pattern.multiDay && pattern.slots.length === 1;

export const editablePatternOptions = (patterns: ShiftPatternEntity[]): ShiftPatternEntity[] =>
  patterns.filter(isPatternEditable);

export const isShiftEditable = (shift: TruckShiftShiftEntity): boolean => !shift.fixed;

const hhmm = (iso: string): string => datetimeDecorator.toHourMinutes(new Date(iso)) ?? '';

const hoursOf = (seconds: number | null): number | '' => (seconds === null ? '' : seconds / 3600);

const ymd = (iso: string): string => datetimeDecorator.toYyyyMmDd(new Date(iso)) ?? '';

export const toEditorValues = (
  shift: TruckShiftShiftEntity | null,
  patterns: ShiftPatternEntity[],
): { values: CellEditorFormValues; patternEditable: boolean } => {
  if (shift === null) {
    return {
      values: { mode: 'pattern', patternId: null, startTime: '09:00', endTime: '18:00', startDate: '', endDate: '', workingHours: '', driverId: null, driverName: '' },
      patternEditable: true,
    };
  }
  const startTime = hhmm(shift.startAt);
  const endTime = hhmm(shift.endAt);
  const startDate = ymd(shift.startAt);
  const endDate = ymd(shift.endAt);
  const workingHours = hoursOf(shift.workingAvailableDurationSeconds);
  const driverId = shift.driver?.id ?? null;
  const driverName = shift.driver?.name ?? '';

  if (shift.shiftPattern === null) {
    return { values: { mode: 'custom', patternId: null, startTime, endTime, startDate, endDate, workingHours, driverId, driverName }, patternEditable: true };
  }
  const patternId = shift.shiftPattern.id;
  const editable = patterns.some((p) => p.id === patternId && isPatternEditable(p));
  if (editable) {
    return { values: { mode: 'pattern', patternId, startTime, endTime, startDate, endDate, workingHours, driverId, driverName }, patternEditable: true };
  }
  // 編集候補外（multiDay/複数スロット）: 便モード保存は不可だが、便名ラベル表示のため patternId は保持する（P1）。
  // 時刻/日付を編集すると CellShiftCard の patch が patternId: null へ落とす＝その時点で custom 化（便から外れる）。
  return { values: { mode: 'custom', patternId, startTime, endTime, startDate, endDate, workingHours, driverId, driverName }, patternEditable: false };
};

const customBody = (values: CellEditorFormValues): CustomBody => {
  const body: CustomBody = {
    start_time: values.startTime, end_time: values.endTime,
    start_date: values.startDate, end_date: values.endDate,
  };
  if (values.workingHours !== '') body.working_available_duration_hours = Number(values.workingHours);
  return body;
};

export const buildEditRequest = (values: CellEditorFormValues): EditRequestBody => {
  if (values.mode === 'pattern') {
    if (values.patternId === null) throw new Error('patternId is required in pattern mode');
    return { shift_pattern_id: values.patternId };
  }
  return { custom: customBody(values) };
};

export const buildAddRequest = (truckId: number, date: string, values: CellEditorFormValues): AddRequestBody => {
  const base = { truck_ids: [truckId], dates: [date], mode: 'add' as const };
  if (values.mode === 'pattern') {
    if (values.patternId === null) throw new Error('patternId is required in pattern mode');
    return { ...base, shift_pattern_id: values.patternId };
  }
  return { ...base, custom: customBody(values) };
};

export const editFieldsChanged = (a: CellEditorFormValues, b: CellEditorFormValues): boolean =>
  a.mode !== b.mode || a.patternId !== b.patternId || a.startTime !== b.startTime
  || a.endTime !== b.endTime || a.startDate !== b.startDate || a.endDate !== b.endDate
  || a.workingHours !== b.workingHours;

export const driverChanged = (a: CellEditorFormValues, b: CellEditorFormValues): boolean =>
  a.driverId !== b.driverId || a.driverName.trim() !== b.driverName.trim();

export type DriverResolution =
  | { kind: 'existing'; driverId: number }
  | { kind: 'unassign' }
  | { kind: 'create'; name: string };

// 保存時のドライバー解決: 既存選択 or フリー入力名が既存と一致 → existing、空 → unassign、未登録名 → create。
// 既存名と一致するフリー入力で無駄に新規作成しないため、候補名との突合を行う。
export const resolveDriver = (
  values: CellEditorFormValues,
  options: { id: number; name: string }[],
): DriverResolution => {
  if (values.driverId !== null) return { kind: 'existing', driverId: values.driverId };
  const name = values.driverName.trim();
  if (name === '') return { kind: 'unassign' };
  const match = options.find((o) => o.name === name);
  return match ? { kind: 'existing', driverId: match.id } : { kind: 'create', name };
};

export type ClearCellSummary = { total: number; deleted: number; failed: number; reasons: string[]; message: string };

export const summarizeClearCell = (
  results: PromiseSettledResult<unknown>[],
  extractReason: (rejection: PromiseRejectedResult) => string,
): ClearCellSummary => {
  const deleted = results.filter((r) => r.status === 'fulfilled').length;
  const rejected = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
  const reasons = Array.from(new Set(rejected.map(extractReason).filter((m) => m !== '')));
  const reasonText = reasons.length > 0 ? reasons.join(' / ') : '配車済み/固定/乗り換え等';
  let message: string;
  if (rejected.length === 0) message = `${deleted}件削除しました`;
  else if (deleted > 0) message = `${deleted}件削除しました（一部は削除できませんでした: ${reasonText}）`;
  else message = `削除できませんでした: ${reasonText}`;
  return { total: results.length, deleted, failed: rejected.length, reasons, message };
};

// backend ShiftEditService の TIME_FORMAT 相当の厳格パターン（99:99 等を弾く。patternForm の緩い TIME_RE とは別）。
export const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export const cellEditorSchema = yup.object({
  mode: yup.mixed<CellEditorMode>().oneOf(['pattern', 'custom']).required(),
  patternId: yup.number().nullable().when('mode', {
    is: 'pattern',
    then: (s) => s.typeError('便を選択してください').required('便を選択してください'),
    otherwise: (s) => s.nullable(),
  }),
  startTime: yup.string().when('mode', {
    is: 'custom',
    then: (s) => s.required('開始時刻を入力してください').matches(TIME_RE, 'HH:MM 形式で入力してください'),
    otherwise: (s) => s.default(''),
  }),
  endTime: yup.string().when('mode', {
    is: 'custom',
    then: (s) => s.required('終了時刻を入力してください').matches(TIME_RE, 'HH:MM 形式で入力してください'),
    otherwise: (s) => s.default(''),
  }),
  startDate: yup.string().required(),
  endDate: yup.string().required(),
  workingHours: yup.mixed<number | ''>().test('positive-or-empty', '労働時間は正の数で入力してください', (v) =>
    v === '' || v === undefined || (typeof v === 'number' && v > 0)),
  driverId: yup.number().nullable(),
  driverName: yup.string().default(''),
}) as yup.ObjectSchema<CellEditorFormValues>;
